import { useEffect, useMemo, useState } from 'react';
import type { NewContact } from '@personical/cc-shared-types';
import { useAuth } from '../auth.js';
import { useApi } from '../components/api-context.js';
import { Avatar } from '../components/Avatar.js';
import {
  COLLEAGUES_SCOPES,
  listColleagues,
  type GraphColleague
} from '../graph.js';

const DEMO_COLLEAGUES: GraphColleague[] = [
  {
    id: 'demo-colleague-1',
    displayName: 'Reception',
    jobTitle: 'Front of house',
    department: 'Operations',
    mail: 'reception@analytical.test',
    businessPhones: ['+44 20 7946 0001']
  },
  {
    id: 'demo-colleague-2',
    displayName: 'Sales Team',
    jobTitle: 'Group',
    department: 'Sales',
    mail: 'sales@analytical.test',
    businessPhones: ['+44 20 7946 0002']
  }
];

export function ColleaguesPage() {
  const api = useApi();
  const { state, getAccessToken } = useAuth();
  const isDemo = state.kind === 'authenticated' && state.user.demo;

  const [search, setSearch] = useState('');
  const [committed, setCommitted] = useState('');
  const [items, setItems] = useState<GraphColleague[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [groupByDept, setGroupByDept] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);

    if (isDemo) {
      const q = committed.trim().toLowerCase();
      const filtered = q
        ? DEMO_COLLEAGUES.filter(
            (c) =>
              c.displayName.toLowerCase().includes(q) ||
              (c.mail ?? '').toLowerCase().includes(q)
          )
        : DEMO_COLLEAGUES;
      setItems(filtered);
      return;
    }

    (async () => {
      const token = await getAccessToken(COLLEAGUES_SCOPES);
      if (!token) {
        if (!cancelled) {
          setError(
            'Could not get a Microsoft Graph token. The app may need User.ReadBasic.All consented for your account.'
          );
        }
        return;
      }
      try {
        const rows = await listColleagues(token, committed || undefined);
        if (!cancelled) setItems(rows);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [committed, isDemo, getAccessToken]);

  const summary = useMemo(() => {
    if (error) return null;
    if (!items) return 'Loading…';
    if (items.length === 0) return committed ? 'No matches.' : 'No colleagues found.';
    return `${items.length} colleague${items.length === 1 ? '' : 's'}`;
  }, [items, error, committed]);

  // Group items by department when the toggle is on. Anyone without a
  // department falls into a "No department" bucket so they're still
  // visible. Sort by department name then by display name.
  const grouped = useMemo(() => {
    if (!items || !groupByDept) return null;
    const map = new Map<string, GraphColleague[]>();
    for (const c of items) {
      const key = c.department?.trim() || 'No department';
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    return [...map.entries()]
      .map(([dept, list]) => ({ dept, list }))
      .sort((a, b) => a.dept.localeCompare(b.dept));
  }, [items, groupByDept]);

  async function copyToContacts(c: GraphColleague) {
    setSavingId(c.id);
    try {
      const input: NewContact = {
        displayName: c.displayName,
        jobTitle: c.jobTitle ?? '',
        department: c.department?.trim() || undefined,
        company: '',
        website: '',
        emails: c.mail
          ? [{ type: 'work', address: c.mail, primary: true }]
          : [],
        phoneNumbers: [
          ...(c.mobilePhone
            ? [{ type: 'mobile' as const, number: c.mobilePhone, primary: true }]
            : []),
          ...((c.businessPhones ?? []).map((p) => ({ type: 'work' as const, number: p })))
        ],
        notes: 'Imported from Microsoft 365 directory.',
        tags: ['colleague'],
        callTreatment: 'normal',
        visibility: 'private', // copy lands in caller's private contacts
        kind: 'person',
        custom: {}
      };
      await api.create(input);
      setSavedIds((prev) => new Set(prev).add(c.id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="text-2xl font-semibold">Colleagues</h2>
        <p className="text-xs text-muted">
          Live from your Microsoft 365 directory — not stored in Cloud Contacts.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setCommitted(search.trim());
        }}
        className="flex gap-2 flex-wrap items-center"
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="input flex-1 min-w-[200px]"
        />
        <button type="submit" className="btn-secondary px-3 py-2 text-sm font-medium">
          Search
        </button>
        {committed && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setCommitted('');
            }}
            className="btn-secondary px-3 py-2 text-sm font-medium"
          >
            Clear
          </button>
        )}
        <label className="inline-flex items-center gap-1.5 text-sm text-mid cursor-pointer ml-auto">
          <input
            type="checkbox"
            checked={groupByDept}
            onChange={(e) => setGroupByDept(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-[color:var(--cc-accent)]"
          />
          Group by department
        </label>
      </form>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">
          {error}
        </div>
      )}

      {summary && <p className="text-xs text-muted">{summary}</p>}

      {items && items.length > 0 && !grouped && (
        <ul className="surface rounded-2xl shadow divide-y divide-divider">
          {items.map((c) => (
            <ColleagueRow
              key={c.id}
              colleague={c}
              isSaved={savedIds.has(c.id)}
              isSaving={savingId === c.id}
              onSave={copyToContacts}
            />
          ))}
        </ul>
      )}

      {grouped && grouped.map(({ dept, list }) => (
        <section key={dept} className="space-y-1.5">
          <h3 className="text-[11px] uppercase tracking-wider text-faint px-1">
            {dept} <span className="text-faint/70 tabular-nums">({list.length})</span>
          </h3>
          <ul className="surface rounded-2xl shadow divide-y divide-divider">
            {list.map((c) => (
              <ColleagueRow
                key={c.id}
                colleague={c}
                isSaved={savedIds.has(c.id)}
                isSaving={savingId === c.id}
                onSave={copyToContacts}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ColleagueRow({
  colleague: c,
  isSaved,
  isSaving,
  onSave
}: {
  colleague: GraphColleague;
  isSaved: boolean;
  isSaving: boolean;
  onSave(c: GraphColleague): void;
}) {
  // Build the secondary line from whichever fields are set — no
  // placeholder dashes, no "—" in front of values.
  const subtitleParts = [c.jobTitle, c.department, c.mail].filter(Boolean) as string[];
  const phoneParts = [c.mobilePhone, c.businessPhones?.[0]].filter(Boolean) as string[];
  return (
    <li className="flex items-start gap-3 px-5 py-4 flex-wrap">
      <Avatar name={c.displayName} />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{c.displayName}</p>
        {subtitleParts.length > 0 && (
          <p className="text-xs text-muted">{subtitleParts.join(' · ')}</p>
        )}
        {phoneParts.length > 0 && (
          <p className="text-xs text-muted">{phoneParts.join(' · ')}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onSave(c)}
        disabled={isSaving || isSaved}
        className="btn-secondary px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        title="Save a private copy to your Cloud Contacts"
      >
        {isSaved ? 'Saved' : isSaving ? 'Saving…' : 'Save to my contacts'}
      </button>
    </li>
  );
}
